import { FormEvent, useState } from "react";
import Preloader from "../../../app-components/preloader/Preloader";
import { Link } from "react-router-dom";
import {
  OnboardingSchema,
  OnboardingType,
} from "../../../interface-and-types/auth.interface";
import apiClient from "../../../api-services/ApiClient";
import PasswordToggle from "../../../app-components/toggle-password/PasswordToggle";
import { SITELINKS } from "../../../app-routes/links";
import { LuCheck } from "react-icons/lu";
import AuthLayout from "../common/AuthLayout";

const Onboarding = () => {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);

  // Access specific parameters
  const token = urlParams.get("token")?.replace(/ /g, "+");
  const email = urlParams.get("email");

  const [serverError, setServerError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [isSuccessful, setIsSuccessful] = useState<boolean>(false);

  // component-state
  const [formData, setFormData] = useState<OnboardingType>({
    confirm_password: "",
    password: "",
  });

  const [errors, setErrors] = useState<
    Partial<Record<keyof OnboardingType, string>>
  >({});

  // handle-input-change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  // handle-login-submit
  const handleRequest = async (e: FormEvent) => {
    e.preventDefault();
    setErrors({});

    const result = OnboardingSchema.safeParse(formData);
    if (result.success) {
      setLoading(true);

      try {
        const data = {
          token: token,
          password: result.data.password,
          confirmPassword: result.data.confirm_password,
        };

        const res = await apiClient.post("Authentication/SetupPassword", data);

        if (res.data.status) {
          setIsSuccessful(true);
        }
      } catch (error: any) {
        setServerError(JSON.stringify(error));
      } finally {
        setLoading(false);
      }
    } else {
      const formattedErrors: Partial<Record<keyof OnboardingType, string>> = {};
      result.error.errors.forEach((err) => {
        if (err.path.length > 0) {
          const key = err.path[0] as keyof OnboardingType;
          formattedErrors[key] = err.message;
        }
      });

      setErrors(formattedErrors);
    }
  };

  // handle-login-submit
  const handleResendSetupLink = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setServerError("");

    try {
      const data = {
        email: email,
      };

      await apiClient.post("Authentication/ResendSetupPasswordLink", data);
    } catch (error: any) {
      setServerError(JSON.stringify(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title={isSuccessful ? "SETUP SUCCESSFUL!" : "ACCOUNT SETUP"}>
      {!isSuccessful && (
        <p className="flex-col text-xs text-slate-600 px-12 pb-5 text-center">
          Welcome to Marine Vessel Inpection Portal (MVIP)
        </p>
      )}

      {isSuccessful ? (
        <div className="p-12 pt-0 flex-col justify-center text-center items-center">
          <div className="flex justify-center text-center items-center mt-5">
            <LuCheck className="text-[150px] text-gray-50 bg-green-500 rounded-full p-6" />
          </div>

          <p className="flex-col text-slate-600 text-xs mt-5">
            Congratulations {email}! <br />
            You have successfully activated your account.
          </p>

          <div className="flex-col mt-6">
            <Link
              to={SITELINKS.MAIN}
              className="text-green-600 text-xs border border-green-600 px-5 py-3 rounded-lg"
            >
              Click here to login
            </Link>
          </div>
        </div>
      ) : (
        <form
          className="p-12 pt-3"
          onSubmit={handleRequest}
          autoComplete="off"
          autoCorrect="off"
        >
          <p className="text-red-600 text-xs text-center mb-2 px-12">
            {serverError}
          </p>

          {serverError && (
            <div className="text-xs text-center ">
              Generate new setup link?{" "}
              <button
                onClick={handleResendSetupLink}
                className="text-gray-600 font-normal text-sm underline mb-2"
              >
                Click here
              </button>
            </div>
          )}

          {/* email input */}
          <div className="flex flex-col text-xs mb-4 relative">
            <label htmlFor="Email" className="mb-1">
              Email Address
            </label>

            <input
              name="email"
              id="email"
              type="email"
              className="w-full bg-gray-50 border border-solid border-gray-400 text-black p-3 rounded-lg focus:outline-1 focus:outline focus:outline-gray-600 font-normal disabled:bg-gray-200 font-medium"
              placeholder="Enter your password"
              disabled
              value={email ?? ""}
            />
          </div>

          {/* password input */}
          <div className="flex flex-col text-xs mb-4 relative">
            <label htmlFor="Password" className="mb-0.5">
              Enter Password
            </label>

            <input
              name="password"
              id="password_input_one"
              type="password"
              onChange={handleInputChange}
              className={`w-full bg-gray-50 border border-solid border-gray-400 text-black p-3 rounded-lg focus:outline-1 focus:outline focus:outline-gray-600 font-normal`}
              placeholder="Enter your password"
            />

            <div className="absolute top-7 right-3">
              <PasswordToggle targetId="password_input_one" />
            </div>

            <span className="text-red-600 text-[10px]">{errors.password}</span>
          </div>

          {/* password input */}
          <div className="flex flex-col text-xs mb-4 relative">
            <label htmlFor="Password" className="mb-0.5">
              Confirm Password
            </label>

            <input
              name="confirm_password"
              id="password_input_two"
              type="password"
              onChange={handleInputChange}
              className={`w-full bg-gray-50 border border-solid border-gray-400 text-black p-3 rounded-lg focus:outline-1 focus:outline focus:outline-gray-600 font-normal`}
              placeholder="Enter your password"
            />

            <div className="absolute top-7 right-3">
              <PasswordToggle targetId="password_input_two" />
            </div>

            <span className="text-red-600 text-[10px]">
              {errors.confirm_password}
            </span>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="disabled:bg-gray-200 disabled:text-gray-600 mt-5 bg-red-600 text-white px-20 py-3 shadow-md hover:shadow-xl w-full rounded-xl text-sm"
          >
            {loading ? "Loading..." : "Continue"}
          </button>
        </form>
      )}

      <Preloader loading={loading} />
    </AuthLayout>
  );
};

export default Onboarding;
